import AHRIToolRelatedProductInfo from "@crescentParts/Components/AHRIToolRelatedProduct/AHRIToolRelatedProductInfo";
import { ProductCarouselProductStyles } from "@crescentParts/Overrides/Components/ProductCarouselProduct";
import CustomApplicationState from "@crescentParts/Store/CustomApplicationState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import useEmblaCarousel from "embla-carousel-react";
import React, { createRef, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        relatedProducts: state.cpeState.ahriRelatedProductState?.relatedProductLines ?? [],
    };
};
type Props = ThemeProps<BaseTheme> & HasShellContext & ReturnType<typeof mapStateToProps>;

export interface RelatedProductCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    mainWrapper?: InjectableCss;
}

export const relatedProductCarouselStyles: RelatedProductCarouselStyles = {
    mainWrapper: {
        css: css`
            background: #f6f4f6;
            /* padding: 53px 80px 60px 80px; */
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 53px 35px 50px 35px;
                        `,
                        null,
                        null,
                        css`
                            padding: 53px 70px 50px 70px;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
            color: ${getColor("text.accent")};
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            position: relative;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            top: 45%;
            border-radius: 50%;
            background: ${getColor("text.link")};
            left: -15px;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            max-width: none;
            width: 40px;
            height: 40px;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            svg {
                stroke: white;
            }
            border: 0;
            padding: 0;
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            top: 45%;
            right: -15px;
            border-radius: 50%;
            background: ${getColor("text.link")};
            display: flex;
            align-items: center;
            justify-content: center;
            max-width: none;
            width: 40px;
            height: 40px;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            svg {
                stroke: white;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            padding-top: 10px;
            padding-bottom: 10px;
        `,
    },
    carouselSlide: {
        css: css`
            background: transparent;
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            border-radius: 12px;
            padding: 0 10px;
        `,
    },
    carouselSlideInner: {
        css: css`
            background: white;
            border-radius: 20px;
            [data-test-selector="productImage"] {
                min-height: 230px;
                max-height: 230px;
                height: 230px;
            }
            width: 100%;
            height: 100%;
            position: relative;
            padding: 15px 20px;
            display: flex;
            flex-direction: column;
            box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.1);
        `,
    },
};

const styles = relatedProductCarouselStyles;

const AHRIToolRelatedProductCarousel = ({ theme, relatedProducts, shellContext }: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = useCallback(() => {
        const maxNumberOfColumns = 4;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else if (window.innerWidth < theme.breakpoints.values[4]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 4;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [4]);

    const [slidesToScroll, setSlidesToScroll] = useState(getSlidesToScroll());

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    useEffect(() => {
        if (!embla || !relatedProducts || relatedProducts.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: isTouchDevice(),
            loop: relatedProducts?.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, relatedProducts]);

    const customCssWrapper = {
        css: css``,
    };

    if (relatedProducts.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...customCssWrapper}>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <GridContainer {...styles.mainContainer} data-test-selector="ahri_relatedProduct_Carousel">
                <GridItem {...styles.prevArrowGridItem}>
                    {relatedProducts.length > slidesToScroll && (
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            aria-label={translate("Previous")}
                            data-test-selector="prevBtn"
                        >
                            <ButtonIcon src={ChevronLeft} />
                        </Button>
                    )}
                </GridItem>
                <GridItem {...styles.carouselGridItem}>
                    <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                {relatedProducts.map((product: any) => (
                                    <StyledWrapper
                                        {...styles.carouselSlide}
                                        key={product.id}
                                        style={{ width: `calc(100% / ${slidesToScroll})` }}
                                    >
                                        <StyledWrapper
                                            {...styles.carouselSlideInner}
                                            id={`product_${product.id}`}
                                            data-test-key={product.id}
                                            data-test-selector="carousel_slide"
                                        >
                                            <AHRIToolRelatedProductInfo
                                                product={product}
                                                showImage={true}
                                                showBrand={true}
                                                showTitle={true}
                                                showPartNumbers={true}
                                                showPrice={true}
                                                showAddToCart={true}
                                                showAddToList={true}
                                                showImageBadges={true}
                                                maximumNumberOfImageBadges={4}
                                                showTextBadges={true}
                                                maximumNumberOfTextBadges={4}
                                                showProductAvailability={true}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.nextArrowGridItem}>
                    {relatedProducts.length > slidesToScroll && (
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            aria-label={translate("Next")}
                            data-test-selector="nextBtn"
                        >
                            <ButtonIcon src={ChevronRight} />
                        </Button>
                    )}
                </GridItem>
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
        </StyledWrapper>
    );
};
export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;
export default connect(mapStateToProps)(withIsInShell(withTheme(AHRIToolRelatedProductCarousel)));
